<script setup lang="ts">
import { computed, ref } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import Button from '@/Components/Utils/Button.vue'
import SelectButton from 'primevue/selectbutton'
import Chart from 'primevue/chart'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faInfoCircle, faSyncAlt } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faInfoCircle, faSyncAlt)

interface Stat {
    label: string
    value: number
    tooltip: string
}

interface RankedRow {
    name: string
    count: number
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    range: number
    range_options: { value: number, label: string }[]
    stats: Record<string, Stat>
    daily: { date: string, label: string, count: number }[]
    by_button: RankedRow[]
    by_page: RankedRow[]
    by_product: RankedRow[]
}>()

const selectedRange = ref(props.range)
const isLoading = ref(false)

const changeRange = (range: number | null) => {
    if (!range || range === props.range) {
        selectedRange.value = props.range
        return
    }

    router.get(route('website.analytics.index'), { range }, {
        preserveScroll: true,
        preserveState: true,
        onStart: () => { isLoading.value = true },
        onFinish: () => { isLoading.value = false },
    })
}

const isRefreshing = ref(false)

const refresh = () => {
    router.reload({
        preserveScroll: true,
        onStart: () => {
            isRefreshing.value = true
            isLoading.value = true
        },
        onFinish: () => {
            isRefreshing.value = false
            isLoading.value = false
        },
    })
}

const dailyChartData = computed(() => ({
    labels: props.daily.map(day => day.label),
    datasets: [
        {
            label: 'Klik WhatsApp',
            data: props.daily.map(day => day.count),
            backgroundColor: '#22c55e',
            borderRadius: 4,
        },
    ],
}))

const dailyChartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } },
        x: { grid: { display: false } },
    },
}

const rankedSections = computed(() => [
    {
        key: 'button',
        title: 'Per tombol',
        tooltip: 'Teks tombol WhatsApp yang diklik, digabung dari semua halaman',
        rows: props.by_button,
    },
    {
        key: 'page',
        title: 'Per halaman',
        tooltip: 'Halaman eatpackku tempat tombol WhatsApp diklik',
        rows: props.by_page,
    },
    {
        key: 'product',
        title: 'Per produk',
        tooltip: 'Klik WhatsApp dari halaman detail produk',
        rows: props.by_product,
    },
])

const barWidth = (count: number, rows: RankedRow[]) => {
    const max = Math.max(...rows.map(row => row.count), 0)

    return max > 0 ? `${Math.max((count / max) * 100, count > 0 ? 2 : 0)}%` : '0%'
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="flex flex-col gap-4 px-4 py-4" :class="{ 'opacity-60': isLoading }">
        <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="text-sm text-gray-500">Klik tombol WhatsApp di website eatpackku</p>
            <div class="flex flex-wrap items-center gap-2">
                <SelectButton
                    v-model="selectedRange"
                    :options="range_options"
                    optionLabel="label"
                    optionValue="value"
                    :allowEmpty="false"
                    v-tooltip.left="'Periode data yang ditampilkan'"
                    @update:modelValue="changeRange"
                />
                <Button
                    type="tertiary"
                    label="Refresh"
                    icon="fal fa-sync-alt"
                    :loading="isRefreshing"
                    v-tooltip.bottom="'Muat ulang data klik terbaru'"
                    @click="refresh"
                />
            </div>
        </div>

        <div class="grid gap-4 sm:grid-cols-3">
            <div v-for="(stat, key) in stats" :key="key" class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="flex items-center gap-x-1 text-xs text-gray-400">
                    {{ stat.label }}
                    <FontAwesomeIcon v-tooltip="stat.tooltip" icon="fal fa-info-circle" class="cursor-pointer" fixed-width aria-hidden="true" />
                </div>
                <div class="mt-1 text-xl font-semibold">{{ useFormatNumber(stat.value) }}</div>
            </div>
        </div>

        <section class="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                Klik per hari
            </div>
            <div class="h-72 px-4 py-4">
                <Chart type="bar" :data="dailyChartData" :options="dailyChartOptions" class="h-full" />
            </div>
        </section>

        <div class="grid gap-4 lg:grid-cols-3">
            <section v-for="section in rankedSections" :key="section.key" class="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div v-tooltip="section.tooltip" class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    {{ section.title }}
                </div>

                <div v-if="!section.rows.length" class="px-6 py-8 text-center text-sm text-gray-400">
                    Belum ada klik di periode ini
                </div>

                <ul v-else class="divide-y divide-gray-100 text-sm">
                    <li v-for="row in section.rows" :key="row.name" class="px-6 py-3">
                        <div class="flex items-center justify-between gap-x-4">
                            <span class="min-w-0 truncate text-gray-600" :title="row.name">{{ row.name }}</span>
                            <span class="shrink-0 font-medium">{{ useFormatNumber(row.count) }}</span>
                        </div>
                        <div class="mt-1.5 h-1.5 rounded-full bg-gray-100">
                            <div class="h-1.5 rounded-full bg-green-500" :style="{ width: barWidth(row.count, section.rows) }" />
                        </div>
                    </li>
                </ul>
            </section>
        </div>
    </div>
</template>
